import type { FC } from 'react';

import type { AssetsShareSchemaWithId } from '@iconik-io/iconik-api/src/services/assets';
import {
  isObjectType,
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_SHARES,
} from '@iconik-io/iconik-api/src/types/object_types';
import {
  ADMIN_SHARE_MANAGEMENT_PAGE_ROUTE,
  MY_SHARES_ROUTE,
} from '@iconik-io/web/src/constants/routes';

import { fetchAssetReviewRequest } from '../../../ducks/asset_review_approvals/thunks';
import { selectActingUserId } from '../../../ducks/auth/selectors';
import { deleteAdminSharesByIds } from '../../../ducks/pages/admin_share_management/thunks';
import { deleteSharesByIds } from '../../../ducks/shares/thunks';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import type { ObjectWithId } from '../../../types/common';
import withContextItems from '../context_items_hoc';
import type {
  ContextItemsHocProps,
  ModalComponentProps,
} from '../modals_manager/types';
import DeleteObjectsModal from './delete_objects';

type Props = ModalComponentProps &
  ContextItemsHocProps<AssetsShareSchemaWithId & { is_approval?: boolean }>;

const getAdditionalBody = (hasReviewShares: boolean, items: ObjectWithId[]) => {
  if (!hasReviewShares) {
    if (items.length === 1) {
      return 'Once deleted, this share cannot be used again and all participants will immediately lose access.';
    } else if (items.length > 1) {
      return 'Once deleted, these shares cannot be used again and all participants will immediately lose access.';
    }
  }
  if (items.length > 1) {
    return 'The selection contains one or more shares connected to a Review Request. Deleting these shares will remove the possibility for users to set approval status using those shares. Any existing approval status will be kept.';
  }
  return 'This share is connected to a Review Request. Deleting it will remove the possibility for users to set approval status using this share. Any existing approval status on the asset will be kept.';
};

const DeleteSharesModal: FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const routeParams = useAppSelector((state) => state.routeParams);
  const shares = useAppSelector((state) => state.entities.shares);
  const objectType = props.item.object_type;
  const objectId = props.item.object_id;

  const userId = useAppSelector(selectActingUserId);
  const isOwner = props.items.every((item) => {
    return item.owner_id === userId;
  });

  const hasReviewShares = props.items.some((item) => item.is_approval);
  const additionalBody = getAdditionalBody(hasReviewShares, props.items);
  return (
    <DeleteObjectsModal
      modalId={props.modalId}
      isOpen={props.isOpen}
      onCancel={props.onCancel}
      onConfirm={props.onConfirm}
      objectType={OBJECT_TYPE_SHARES}
      deleteEntityByIds={async (ids: string[]) => {
        if (
          (routeParams.route === MY_SHARES_ROUTE && isOwner) ||
          routeParams.route === ADMIN_SHARE_MANAGEMENT_PAGE_ROUTE
        ) {
          await dispatch(deleteAdminSharesByIds(ids));
        } else {
          await Promise.all(
            ids.map(async (id) => {
              const shareObjectId = shares[id].object_id;
              const shareObjectType = shares[id].object_type;
              if (
                shareObjectType &&
                isObjectType(shareObjectType) &&
                shareObjectId
              ) {
                await dispatch(
                  deleteSharesByIds(shareObjectType, shareObjectId, [id]),
                );
              }
            }),
          );

          if (objectType === OBJECT_TYPE_ASSETS && hasReviewShares) {
            await dispatch(fetchAssetReviewRequest(objectId));
          }
        }
      }}
      additionalBody={additionalBody}
    />
  );
};

export default withContextItems(DeleteSharesModal);
